import { skipToken, useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Button, buttonClasses } from '../components/ui/button.js'
import { CheckboxField, SelectField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { cn } from '../lib/cn.js'
import { useAuth } from '../lib/auth.js'
import { listAttributes } from '../lib/attributes.js'
import { listEvents } from '../lib/events.js'
import {
  activeFilterCount,
  emptyFilter,
  listItems,
  toQuery,
  type ItemFilterState,
} from '../lib/items.js'
import { listUsers } from '../lib/users.js'
import { listValueListItems, listValueLists } from '../lib/value-lists.js'

const PAGE_SIZE = 25

/**
 * Everything that has been captured.
 *
 * The columns come from the catalogue's showInList flag rather than from a
 * fixed list here, so a field switched on this morning appears without a
 * deployment — the same rule that governs the capture form.
 */
export function ItemsPage() {
  const strings = t().items
  const { hasRole } = useAuth()

  // Arriving from a fair's card with its id in the address. Used as the
  // starting filter, not as a lock: the person can widen it from here.
  const { eventId } = useSearch({ from: '/authenticated/artikel' })
  const [filter, setFilter] = useState<ItemFilterState>({
    ...emptyFilter(),
    ...(eventId ? { eventId } : {}),
  })
  const [page, setPage] = useState(0)
  const [showFilters, setShowFilters] = useState(eventId !== undefined)

  // Any change to the filter goes back to the first page. Staying on page four
  // of a narrower result set shows an empty table and looks like no matches.
  function change(next: Partial<ItemFilterState>) {
    setFilter((current) => ({ ...current, ...next }))
    setPage(0)
  }

  const query = useQuery({
    queryKey: ['items', filter, page],
    queryFn: () => listItems(toQuery(filter, PAGE_SIZE, page * PAGE_SIZE)),
  })

  const events = useQuery({ queryKey: ['events'], queryFn: () => listEvents() })
  const attributes = useQuery({ queryKey: ['attributes', false], queryFn: () => listAttributes() })
  const valueLists = useQuery({ queryKey: ['value-lists'], queryFn: listValueLists })

  const supplierListId = valueLists.data?.find((list) => list.key === 'suppliers')?.id
  const suppliers = useQuery({
    queryKey: ['value-list-items', supplierListId, 'all'],
    queryFn:
      supplierListId === undefined
        ? skipToken
        : () => listValueListItems(supplierListId, { limit: 200 }),
  })

  // The capturer filter needs the user list, which only an admin may read. A
  // capturer simply does not get that one control rather than a failed request.
  const users = useQuery({
    queryKey: ['users'],
    queryFn: hasRole('admin') ? () => listUsers() : skipToken,
  })

  const columns = useMemo(
    () =>
      (attributes.data ?? [])
        // Same rule as the API applies to listValues: only jsonb-backed fields
        // become dynamic columns. Barcode, supplier and the timestamps have
        // their own place in the table already.
        .filter(
          (attribute) => attribute.showInList && attribute.active && attribute.storage === 'jsonb',
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [attributes.data],
  )

  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const filterCount = activeFilterCount(filter)

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{strings.title}</h1>
          <p className="text-text-muted text-sm">{strings.subtitle}</p>
        </div>
        {hasRole('admin') && (
          <Link to="/artikel/neu" className={buttonClasses()}>
            {strings.create}
          </Link>
        )}
      </header>

      {query.isError && <Alert tone="danger">{strings.loadError}</Alert>}

      <section className="border-border bg-surface-raised space-y-4 rounded-lg border p-4">
        <div className="flex flex-wrap items-end gap-3">
          <TextField
            label={strings.search}
            hint={strings.searchHint}
            className="min-w-64"
            value={filter.search}
            onChange={(event) => change({ search: event.target.value })}
          />

          <Button
            variant="secondary"
            data-testid="filter-toggle"
            aria-expanded={showFilters}
            onClick={() => setShowFilters((open) => !open)}
          >
            {strings.filters}
            {filterCount > 0 && ` (${String(filterCount)})`}
          </Button>

          {filterCount > 0 && (
            <Button
              variant="secondary"
              onClick={() => {
                setFilter(emptyFilter())
                setPage(0)
              }}
            >
              {strings.clearFilters}
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="border-border grid gap-4 border-t pt-4 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              label={strings.filterEvent}
              value={filter.eventId}
              options={[
                { value: '', label: strings.allEvents },
                ...(events.data ?? []).map((event) => ({
                  value: event.id,
                  label: `${event.code} — ${event.name}`,
                })),
              ]}
              onChange={(event) => change({ eventId: event.target.value })}
            />

            <SelectField
              label={strings.filterSupplier}
              value={filter.supplierItemId}
              options={[
                { value: '', label: strings.allSuppliers },
                ...(suppliers.data?.items ?? []).map((entry) => ({
                  value: entry.id,
                  label: entry.label,
                })),
              ]}
              onChange={(event) => change({ supplierItemId: event.target.value })}
            />

            {hasRole('admin') && (
              <SelectField
                label={strings.filterCapturer}
                value={filter.createdByUserId}
                options={[
                  { value: '', label: strings.allCapturers },
                  ...(users.data?.users ?? []).map((user) => ({
                    value: user.id,
                    label: user.name,
                  })),
                ]}
                onChange={(event) => change({ createdByUserId: event.target.value })}
              />
            )}

            <TextField
              label={strings.filterFrom}
              type="date"
              value={filter.capturedFrom}
              onChange={(event) => change({ capturedFrom: event.target.value })}
            />

            <TextField
              label={strings.filterTo}
              type="date"
              value={filter.capturedTo}
              onChange={(event) => change({ capturedTo: event.target.value })}
            />

            <div className="flex flex-col justify-end gap-2">
              <CheckboxField
                label={strings.onlyNeedsReview}
                checked={filter.needsReviewOnly}
                onChange={(event) => change({ needsReviewOnly: event.target.checked })}
              />
              <CheckboxField
                label={strings.includeDeleted}
                hint={strings.includeDeletedHint}
                checked={filter.includeDeleted}
                onChange={(event) => change({ includeDeleted: event.target.checked })}
              />
            </div>
          </div>
        )}
      </section>

      <p className="text-text-muted text-sm">
        {query.isPending
          ? t().app.loading
          : total === 1
            ? strings.countOne
            : format(strings.countMany, { count: total })}
      </p>

      {!query.isPending && total === 0 ? (
        <p className="border-border rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
          {filterCount > 0 ? strings.emptyFiltered : strings.empty}
        </p>
      ) : (
        <div className="border-border bg-surface-raised overflow-x-auto rounded-lg border">
          <table className="w-full text-sm" data-testid="item-table">
            <thead className="border-border bg-surface-sunken border-b text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{strings.shortCode}</th>
                <th className="px-3 py-2 font-medium">{strings.event}</th>
                <th className="px-3 py-2 font-medium">{strings.supplier}</th>
                {columns.map((column) => (
                  <th key={column.key} className="px-3 py-2 font-medium">
                    {column.label}
                  </th>
                ))}
                <th className="px-3 py-2 font-medium">{strings.photos}</th>
                <th className="px-3 py-2 font-medium">{strings.captured}</th>
              </tr>
            </thead>
            <tbody>
              {(query.data?.items ?? []).map((item) => (
                <tr
                  key={item.id}
                  className={cn(
                    'border-border hover:bg-surface-sunken border-b last:border-0',
                    item.deletedAt !== null && 'opacity-55',
                  )}
                >
                  <td className="px-3 py-2">
                    <Link
                      to="/artikel/$itemId"
                      params={{ itemId: item.id }}
                      className="text-accent font-medium hover:underline"
                    >
                      {item.shortCode ?? strings.noShortCode}
                    </Link>
                    <span className="ml-2 inline-flex gap-1">
                      {item.needsReview && <Badge tone="warning">{strings.needsReview}</Badge>}
                      {item.deletedAt !== null && <Badge tone="danger">{strings.deleted}</Badge>}
                    </span>
                    {item.barcode !== null && (
                      <span className="text-text-muted block text-xs">{item.barcode}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{item.eventCode}</td>
                  <td className="px-3 py-2">{item.supplierLabel ?? ''}</td>
                  {columns.map((column) => (
                    <td key={column.key} className="max-w-56 truncate px-3 py-2">
                      {item.listValues[column.key] ?? ''}
                    </td>
                  ))}
                  <td className="px-3 py-2">{item.photoCount > 0 ? item.photoCount : ''}</td>
                  <td className="text-text-muted px-3 py-2 whitespace-nowrap">
                    {new Date(item.clientCreatedAt).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-between">
          <Button variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
            {strings.previous}
          </Button>
          <span className="text-text-muted text-sm">
            {format(strings.page, { page: page + 1, pages: pageCount })}
          </span>
          <Button
            variant="secondary"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage(page + 1)}
          >
            {strings.next}
          </Button>
        </div>
      )}
    </div>
  )
}
